/**
 * ConfirmEmail - the page the "confirm your address" link lands on
 * ----------------------------------------------------------------
 * Somebody has just clicked a link in their inbox. They may be on a phone,
 * they are quite likely not signed in there, and they want one thing: to be
 * told it worked and to get on with it.
 *
 * So this page confirms on arrival rather than showing a button.
 *
 * THAT IS THE OPPOSITE OF WithdrawConsent.jsx, DELIBERATELY.
 * That page does nothing until a button is pressed, because mail scanners
 * follow links to check them and a scanner must never be able to withdraw a
 * parent's consent. Here, a scanner at the recipient's own mail server
 * following the link still proves the only thing being proved - that mail
 * sent to this address reaches somewhere the account holder controls. The
 * cost of an extra button is real and the risk it would remove is not.
 *
 * It does not require a login for the same reason. The token in the link is
 * what identifies the account; asking somebody to sign in first, on a phone,
 * to finish a step they were told was one click, is where people stop.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle } from 'lucide-react';

import { authApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import Loader from '../components/Loader';
import { useI18n } from '../hooks/useI18n';

export default function ConfirmEmail() {
  const { t } = useI18n();
  const { token } = useParams();
  const { user, refresh } = useAuth();

  const [state, setState] = useState({ loading: true, error: '', firstName: '' });

  useEffect(() => {
    let cancelled = false;

    authApi
      .confirmEmail(token)
      .then((data) => {
        if (cancelled) return;
        setState({ loading: false, error: '', firstName: data.firstName || '' });

        // If they happen to be signed in in this browser, pull the account
        // again so the banner disappears and the buttons unlock without them
        // having to work out that a reload is needed. Failing is fine - they
        // are often not signed in here at all, which is the normal case.
        if (user && typeof refresh === 'function') refresh().catch(() => {});
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, error: err.message, firstName: '' });
      });

    return () => {
      cancelled = true;
    };
    // Deliberately runs once for the token. Adding `user` here would re-fire
    // it the moment the refresh above lands, and the second attempt always
    // fails - the token is single-use, so it would replace "thank you" with
    // "this link is not valid any more" a second after the person read it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (state.loading) return <Loader />;

  return (
    <div className="container section" style={{ maxWidth: 560 }}>
      <div className="card" style={{ textAlign: 'center' }}>
        {state.error ? (
          <>
            <AlertTriangle size={40} className="text-warning" aria-hidden="true" />
            <h1 style={{ marginTop: 'var(--space-sm)' }}>{t('confirmEmail.failedTitle')}</h1>
            <p className="text-muted">{state.error}</p>
            <p className="text-muted">
              {t('confirmEmail.alreadyDone')}
            </p>
            <Link to="/login" className="btn btn-primary btn-block" style={{ marginTop: 'var(--space-md)' }}>
              {t('confirmEmail.goToLogin')}
            </Link>
          </>
        ) : (
          <>
            <CheckCircle2 size={40} className="text-success" aria-hidden="true" />
            <h1 style={{ marginTop: 'var(--space-sm)' }}>
              {state.firstName ? t('confirmEmail.thanksNamed', { name: state.firstName }) : t('confirmEmail.thanks')}
            </h1>
            <p className="text-muted">
              {t('confirmEmail.done')}
            </p>
            <Link to="/dashboard" className="btn btn-primary btn-block" style={{ marginTop: 'var(--space-md)' }}>
              {t('confirmEmail.goToDashboard')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
